import _ from 'underscore';

export const savedTaskItemTypes = {
  Template: 'ReportUITemplate',
  CustomerTemplate: 'ReportUICustomerTemplate',
  AccountTemplate: 'ReportUIAccountTemplate',
};

const sharedTemplateTypes = [
  savedTaskItemTypes.CustomerTemplate,
  savedTaskItemTypes.AccountTemplate,
];

export const isValidSaveType =
  taskItemType =>
    _.chain(savedTaskItemTypes).values(savedTaskItemTypes).contains(taskItemType).value();

export const getSaveType = ({
  IsReportsMergeEnabled, IsInternalUser, accountScope, taskItem,
}) => {
  const taskItemType = _.result(taskItem, 'Type', 'invalid-type');
  const isSavedTaskItemType = isValidSaveType(taskItemType);
  if (isSavedTaskItemType) {
    return taskItemType;
  } else if (IsReportsMergeEnabled && !IsInternalUser) {
    return accountScope ? savedTaskItemTypes.AccountTemplate : savedTaskItemTypes.CustomerTemplate;
  }
  return savedTaskItemTypes.Template;
};

export function isSharedTemplateType(type) {
  return _.contains(sharedTemplateTypes, type);
}

export const ScopeFilterLevel = {
  None: 'None',
  User: 'User',
  Customer: 'Customer',
  Account: 'Account',
  Campaign: 'Campaign',
};

export function getScopeFilterLevel(mtScopeFilterLevel) {
  const mtEnum = _.result(mtScopeFilterLevel, 'enum', []);

  if (_.contains(mtEnum, ScopeFilterLevel.Campaign)) {
    return ScopeFilterLevel.Campaign;
  } else if (_.contains(mtEnum, ScopeFilterLevel.Account)) {
    return ScopeFilterLevel.Account;
  } else if (_.contains(mtEnum, ScopeFilterLevel.Customer)) {
    return ScopeFilterLevel.Customer;
  } else if (_.contains(mtEnum, ScopeFilterLevel.User)) {
    return ScopeFilterLevel.User;
  }

  return ScopeFilterLevel.None;
}

export function getScopeSelectorText({
  i18n, scopeSelectorInfo, reportSchema, history,
}) {
  const ALL_ACCOUNT_TEXT = i18n.getString(_TL_('All accounts'));
  const MULTIPLE_ACCOUNTS_OR_CAMPAIGNS_TEXT = i18n.getString(_TL_('Multiple accounts or campaigns'));
  const MULTIPLE_ACCOUNTS_TEXT = i18n.getString(_TL_('Multiple accounts'));
  const MULTIPLE_MANAGER_ACCOUNTS_TEXT = i18n.getString(_TL_('Multiple manager accounts'));
  const USER_ID_EQUALS_TEXT = userIdText => i18n.getString(_TL_('User id equals ')) + userIdText.toString();
  const EMPTY_STRING = '';

  const scopeFilterLevelOfCurrentReport = getScopeFilterLevel(reportSchema.ScopeFilterLevel);

  const { aid, cid } = history.query;
  // If there is aid, it is account scope, otherwise it is customer scope.
  // NOTE: `customer` contains `accounts`, which contains `campaigns`
  const isCurrentAccountScope = !!aid;

  const isNothingSelected =
    (scopeSelectorInfo.CustomerScopes ? scopeSelectorInfo.CustomerScopes.length : 0) === 0 &&
    (scopeSelectorInfo.AccountIds ? scopeSelectorInfo.AccountIds.length : 0) === 0 &&
    (scopeSelectorInfo.CampaignReportScopes ? scopeSelectorInfo.CampaignReportScopes.length : 0) === 0 &&
    (scopeSelectorInfo.UserScopes ? scopeSelectorInfo.UserScopes.length : 0) === 0;

  if (isNothingSelected) {
    return ALL_ACCOUNT_TEXT;
  }

  const sourceArray = isCurrentAccountScope ? scopeSelectorInfo.AccountIds : scopeSelectorInfo.CustomerScopes;
  const targetArray = isCurrentAccountScope ? [aid] : [cid];
  if (_.isEqual(sourceArray, targetArray)) {
    return ALL_ACCOUNT_TEXT;
  }

  switch (scopeFilterLevelOfCurrentReport) {
    case ScopeFilterLevel.Customer:
      return MULTIPLE_MANAGER_ACCOUNTS_TEXT;
    case ScopeFilterLevel.Campaign:
      return MULTIPLE_ACCOUNTS_OR_CAMPAIGNS_TEXT;
    case ScopeFilterLevel.Account:
      return MULTIPLE_ACCOUNTS_TEXT;
    case ScopeFilterLevel.User:
      return USER_ID_EQUALS_TEXT(scopeSelectorInfo.UserScopes.join(', '));
    case ScopeFilterLevel.None:
      return EMPTY_STRING;
    default:
      throw new Error('unhandled ScopeFilterLevel');
  }
}

export function getDefaultMultiAccountSelectorInfo(reportSchema, history) {
  const { aid, cid, uid } = history.query;
  // If there is aid, it is account scope, otherwise it is customer scope.
  const isCurrentAccountScope = !!aid;
  const scopeFilterLevelOfCurrentReport = getScopeFilterLevel(reportSchema.ScopeFilterLevel);
  // Reference this link to understand the logic:
  // https://microsoft.sharepoint.com/teams/BingAds/_layouts/OneNote.aspx?id=%2Fteams%2FBingAds%2FShared%20Documents%2FTeams%2FWeb%20UI%2FWiki%2FWebUI_SharedNotes&wd=target%28Reporting%2FReport%20revamp.one%7C2822FA52-2B1C-4F9D-83D9-DCB27E072A4C%2FAccount%20campaign%20selector%20behavior%7C6564DB0E-4BAE-481F-B36F-A4EE742ACE82%2F%29

  let CustomerScopes = [];
  let AccountIds = [];
  const CampaignReportScopes = [];
  let UserScopes = [];

  switch (scopeFilterLevelOfCurrentReport) {
    case ScopeFilterLevel.Customer:
      CustomerScopes = [cid];
      break;
    case ScopeFilterLevel.Campaign:
    case ScopeFilterLevel.Account:
      CustomerScopes = isCurrentAccountScope ? [] : [cid];
      AccountIds = isCurrentAccountScope ? [aid] : [];
      break;
    case ScopeFilterLevel.User:
      UserScopes = [uid];
      break;
    case ScopeFilterLevel.None:
      // Fix bug https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/4063939
      // CustomerId is required in current MT implementation. MT resolves CustomerId to all accounts that belong to the customer, and load data from DB shards the accounts belong to. But MT does not filter data by the CustomerId.
      // The current behavior is strange, but MT does not get time to fix. From UI side we have to add current customer id to run through the request.
      CustomerScopes = [cid];
      break;
    default:
      throw new Error('unhandled ScopeFilterLevel');
  }

  return {
    CustomerScopes,
    AccountIds,
    CampaignReportScopes,
    UserScopes,
  };
}

export function shouldShowUserScopeInput(mtScopeFilterLevel) {
  return getScopeFilterLevel(mtScopeFilterLevel) === ScopeFilterLevel.User;
}

export function shouldShowSelector(mtScopeFilterLevel, scope) {
  const scopeFilterLevel = getScopeFilterLevel(mtScopeFilterLevel);
  const { aid } = scope.values;
  if (scopeFilterLevel === ScopeFilterLevel.Account && aid) {
    // In this situation, should always return current account ID.
    return false;
  }

  return _.contains([
    ScopeFilterLevel.Customer,
    ScopeFilterLevel.Account,
    ScopeFilterLevel.Campaign,
  ], scopeFilterLevel);
}

export function getUserIdFromScopeSelector(scopeSelector) {
  const { UserScopes } = scopeSelector;
  const userId = (UserScopes && UserScopes[0]) || undefined;
  return userId;
}

export function convertUserIdToScopeSelector(userScope) {
  return { UserScopes: Number.isNaN(userScope) ? [] : [userScope] };
}
